import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CalendarCheck, ChevronDown, ExternalLink, Globe, History, LifeBuoy, LogOut, Settings, Wallet } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from 'components/ui/dropdown-menu';
import { Avatar } from 'components/ui/avatar';
import { useAuthContext } from 'modules/auth/context';
import { formatGroupedNumber } from 'lib/dateFormat';
import { useLanguageStore, type Language } from 'store/language.store';
import { IS_IO } from 'lib/site';

const LANGUAGES: { code: Language; label: string }[] = [
  { code: 'uz', label: "O'zbek" },
  { code: 'en', label: 'English' },
  { code: 'ru', label: 'Русский' },
];

const isLanguage = (value: string): value is Language => LANGUAGES.some(({ code }) => code === value);

const TELEGRAM_SUPPORT = 'https://t.me/yoqub_sobirjonov';

export function PortalAccountMenu({ trigger }: { trigger?: React.ReactNode }) {
  const { t } = useTranslation();
  const { state, methods } = useAuthContext();
  const { language, setLanguage } = useLanguageStore();
  const name = state.user?.name || state.user?.email || '';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {trigger ?? (
          <button
            type="button"
            aria-label={t('portal.account')}
            className="flex min-h-11 min-w-11 shrink-0 items-center justify-center gap-1 rounded-full p-1 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:min-h-10 md:pr-1.5"
          >
            <Avatar name={name} src={state.user?.avatarUrl} size="sm" isCurrentUser />
            <ChevronDown size={14} className="hidden text-muted-foreground/70 md:block" aria-hidden="true" />
          </button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <div className="px-2.5 py-2.5">
          <p className="truncate text-sm font-semibold">{name}</p>
          <p className="truncate text-xs text-muted-foreground">
            {state.user?.email || state.user?.phone}
          </p>
          {/* The so'm balance pays for marketplace tests: examing.uz only. */}
          {!IS_IO && (
            <div className="mt-2.5 flex items-center justify-between rounded-md bg-muted px-2.5 py-1.5">
              <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <Wallet size={13} className="shrink-0" aria-hidden="true" />
                {t('portal.balance', 'Balans')}
              </span>
              <span className="text-xs font-semibold tabular-nums text-foreground">
                {formatGroupedNumber(state.user?.balanceSom ?? 0)}{' '}
                <span className="font-normal text-muted-foreground">{t('portal_tests_currency')}</span>
              </span>
            </div>
          )}
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/portal/settings" className="flex min-h-10 items-center gap-2">
            <Settings size={16} aria-hidden="true" />
            {t('settings')}
          </Link>
        </DropdownMenuItem>
        {!IS_IO && (
          <DropdownMenuItem asChild>
            <Link to="/portal/settings/plan" className="flex min-h-10 items-center gap-2">
              <CalendarCheck size={16} aria-hidden="true" />
              {t('studyPlan.tab')}
            </Link>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem asChild>
          <Link to="/portal/settings/history" className="flex min-h-10 items-center gap-2">
            <History size={16} aria-hidden="true" />
            {t('portal.results_history')}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <a
            href={TELEGRAM_SUPPORT}
            target="_blank"
            rel="noopener noreferrer"
            className="flex min-h-10 items-center gap-2 cursor-pointer"
          >
            <LifeBuoy size={16} aria-hidden="true" />
            <span>{t('usermenu_support', "Qo'llab-quvvatlash")}</span>
            <ExternalLink size={14} className="ml-auto text-muted-foreground/60" aria-hidden="true" />
          </a>
        </DropdownMenuItem>
        <DropdownMenuSub>
          {/* The trigger's own chevron carries ml-auto; zero it so the language name takes the free space and sits beside it. */}
          <DropdownMenuSubTrigger
            aria-label={t('portal_nav_change_language')}
            className="min-h-10 gap-2 [&>svg:last-child]:ml-0"
          >
            <Globe size={16} aria-hidden="true" />
            <span>{t('language')}</span>
            <span className="ml-auto mr-1 truncate text-xs font-medium text-muted-foreground">
              {LANGUAGES.find(({ code }) => code === language)?.label}
            </span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuRadioGroup value={language} onValueChange={(code) => isLanguage(code) && setLanguage(code)}>
              {LANGUAGES.map(({ code, label }) => (
                <DropdownMenuRadioItem key={code} value={code} className="min-h-10">
                  {label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => methods.logout()} className="min-h-10 gap-2">
          <LogOut size={16} aria-hidden="true" />
          {t('portal.log_out')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
